import React from "react";
import { Button, InputNumber, Select, Tooltip } from "antd";
import { PlusCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { Option } from "antd/es/mentions";

// Per-line utilization task allocation panel. Each row references a handover
// task (`handoverTaskId`) and splits the line's utilized / return / damaged
// qty across that task. Shared by the utilization editor and the read-only
// utilization view.

const rowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
  alignItems: "flex-end",
  padding: "10px 12px",
  border: "1px solid #f0f0f0",
  borderRadius: 6,
  marginBottom: 10,
  background: "#fafafa",
};

const qtyFieldStyle: React.CSSProperties = { minWidth: 120, flex: "0 1 120px" };

const renderTaskLabel = (opt: any, task: any) => {
  if (opt) {
    return `${opt.taskName}${opt.boqName ? ` (${opt.boqName})` : ""}`;
  }
  if (task?.taskName) {
    return `${task.taskName}${task.boqName ? ` (${task.boqName})` : ""}`;
  }
  return task?.handoverTaskId != null
    ? `Handover Task #${task.handoverTaskId}`
    : "NA";
};

const EditableRow: React.FC<any> = ({
  task,
  index,
  handoverTaskOptions,
  inputOnly,
  onUpdate,
  onRemove,
}) => {
  const opt = (handoverTaskOptions || []).find(
    (o: any) => o.handoverTaskId === task.handoverTaskId
  );
  return (
    <div style={rowStyle}>
      <div style={{ fontWeight: 500, color: "#595959", minWidth: 28 }}>
        #{index + 1}
      </div>

      <div style={{ minWidth: 220, flex: "1 1 220px" }}>
        <div className="data-label mb-1">Handover Task</div>
        {inputOnly ? (
          <div className="data-value">{renderTaskLabel(opt, task)}</div>
        ) : (
          <Select
            placeholder="Select Handover Task"
            showSearch
            optionFilterProp="children"
            allowClear
            style={{ width: "100%" }}
            value={task.handoverTaskId}
            onChange={(val) => onUpdate({ handoverTaskId: val ?? undefined })}
          >
            {(handoverTaskOptions || []).map((o: any) => (
              <Option key={o.handoverTaskId} value={o.handoverTaskId}>
                {o.taskName}
                {o.boqName ? ` (${o.boqName})` : ""}
              </Option>
            ))}
          </Select>
        )}
      </div>

      <div style={qtyFieldStyle}>
        <div className="data-label mb-1">Utilized</div>
        <InputNumber
          min={0}
          step={0.001}
          placeholder="0"
          style={{ width: "100%" }}
          value={task.utilizedQty}
          onChange={(val) => onUpdate({ utilizedQty: val })}
        />
      </div>

      <div style={qtyFieldStyle}>
        <div className="data-label mb-1">Returned</div>
        <InputNumber
          min={0}
          step={0.001}
          placeholder="0"
          style={{ width: "100%" }}
          value={task.returnQty}
          onChange={(val) => onUpdate({ returnQty: val })}
        />
      </div>

      <div style={qtyFieldStyle}>
        <div className="data-label mb-1">Damaged</div>
        <InputNumber
          min={0}
          step={0.001}
          placeholder="0"
          style={{ width: "100%" }}
          value={task.damagedQty}
          onChange={(val) => onUpdate({ damagedQty: val })}
        />
      </div>

      {!inputOnly && (
        <Tooltip title="Remove task">
          <Button danger type="text" icon={<DeleteOutlined />} onClick={onRemove} />
        </Tooltip>
      )}
    </div>
  );
};

const ReadOnlyRow: React.FC<any> = ({ task, index, handoverTaskOptions }) => {
  const opt = (handoverTaskOptions || []).find(
    (o: any) => o.handoverTaskId === task.handoverTaskId
  );
  return (
    <div style={rowStyle}>
      <div style={{ fontWeight: 500, color: "#595959", minWidth: 28 }}>
        #{index + 1}
      </div>
      <div style={{ minWidth: 220, flex: "1 1 220px" }}>
        <div className="data-label mb-1">Handover Task</div>
        <div className="data-value">{renderTaskLabel(opt, task)}</div>
      </div>
      <div style={qtyFieldStyle}>
        <div className="data-label mb-1">Utilized</div>
        <div className="data-value">{task.utilizedQty ?? "NA"}</div>
      </div>
      <div style={qtyFieldStyle}>
        <div className="data-label mb-1">Returned</div>
        <div className="data-value">{task.returnQty ?? "NA"}</div>
      </div>
      <div style={qtyFieldStyle}>
        <div className="data-label mb-1">Damaged</div>
        <div className="data-value">{task.damagedQty ?? "NA"}</div>
      </div>
    </div>
  );
};

export interface UtilizationTaskPanelProps {
  tasks: any[];
  handoverTaskOptions?: any[];
  readOnly?: boolean;
  // When true: no Add Task, no Remove, no task picker — render one row per
  // pre-populated task and only let the user edit the qty inputs.
  inputOnly?: boolean;
  onAddTask?: () => void;
  onUpdateTask?: (taskKey: string, changes: any) => void;
  onRemoveTask?: (taskKey: string) => void;
}

export const UtilizationTaskPanel: React.FC<UtilizationTaskPanelProps> = ({
  tasks,
  handoverTaskOptions = [],
  readOnly = false,
  inputOnly = false,
  onAddTask,
  onUpdateTask,
  onRemoveTask,
}) => {
  return (
    <div style={{ padding: "4px 8px 12px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span style={{ fontWeight: 600 }}>Task-wise Utilization</span>
        {!readOnly && !inputOnly && onAddTask && (
          <Button
            type="dashed"
            icon={<PlusCircleOutlined />}
            onClick={onAddTask}
            disabled={!handoverTaskOptions || handoverTaskOptions.length === 0}
          >
            Add Task
          </Button>
        )}
      </div>

      {tasks.length === 0 ? (
        <div style={{ color: "#8c8c8c", fontStyle: "italic" }}>
          {readOnly
            ? "No tasks for this line."
            : inputOnly
              ? "No handover tasks available for this line."
              : "No tasks added. Click \"Add Task\" to split utilized / returned / damaged qty across handover tasks."}
        </div>
      ) : (
        tasks.map((task: any, index: number) =>
          readOnly ? (
            <ReadOnlyRow
              key={task.key || index}
              task={task}
              index={index}
              handoverTaskOptions={handoverTaskOptions}
            />
          ) : (
            <EditableRow
              key={task.key}
              task={task}
              index={index}
              handoverTaskOptions={handoverTaskOptions}
              inputOnly={inputOnly}
              onUpdate={(changes: any) => onUpdateTask?.(task.key, changes)}
              onRemove={() => onRemoveTask?.(task.key)}
            />
          )
        )
      )}
    </div>
  );
};

export const makeUtilizationTaskKey = () =>
  `utask-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export default UtilizationTaskPanel;
